import { CandidateField, CsbFileItem, isFileItem } from "../models";
import { Observable } from 'rxjs';

export interface FormServiceElement {
    el?: HTMLElement;
    name: string;
    validationMsgEnabled: boolean;
    isInvalid(): Promise<false | { name: string, msg: string }>;
    reset?(): Promise<void>;
    readonly value?: boolean | string;
    readonly values?: string[];
    files?: CsbFileItem[];
    type?: string;
    readonly?: boolean;
    required?: boolean;
    requiredMsg?: string;
    emailInvalidMsg?: string;
    numberInvalidMsg?: string;
    validateYesMsg?: string;
    minlength?: number;
    minlengthMsg?: string;
    maxlength?: number;
    maxlengthMsg?: string;
    multiple?: boolean;
    isVisible?(): Promise<boolean>;
    updateValue(value: boolean | string): void;
    updateValues(values: string[]): void;
}

export type SingleValueType = undefined | boolean | string;
export type AllValueTypes = SingleValueType | string[] | CsbFileItem[];
export type HTMLFormServiceElement = HTMLElement & FormServiceElement;

export interface FormFieldValue {
    value?: SingleValueType,
    values?: string[],
    files?: CsbFileItem[]
}

export interface FormFieldChangedEvent extends FormFieldValue {
    valid: boolean,
    name: string
}

export class FormService {

    elementsAll = [
        'csb-field', '[csb-field]',
        'csb-picklist', '[csb-picklist]',
        'csb-upload', '[csb-upload]',
        'csb-field-conditional'
    ];

    constructor(public host: (HTMLElement | string)) { }

    getHostElement(): HTMLElement | null {
        if (typeof this.host === 'string') {
            return document.getElementById(this.host);
        }
        return this.host;
    }

    querySelectorAll<T extends Element>(tags: string[] = this.elementsAll): T[] {
        let query = tags.join(', ');
        let host = this.getHostElement();
        if (!host) return [];

        const shadowRootQuery: T[] = Array.from(host.shadowRoot?.querySelectorAll(query) || []);
        const normalQuery: T[] = Array.from(host.querySelectorAll(query));

        return [...shadowRootQuery, ...normalQuery];
    }

    protected async filterReadonlyFields(all: HTMLFormServiceElement[]) {
        return all.filter(el => !el.readonly);
    }

    protected async filterConditionallyHiddenFields(all: HTMLFormServiceElement[]) {
        async function recursiveFilterForHiddenFieldConditional(node: HTMLFormServiceElement): Promise<boolean> {
            let parent = node.parentElement;
            let closest = parent?.closest<HTMLFormServiceElement>('csb-field-conditional');
            if (!closest || !all.includes(closest)) return true;
            if (!await closest.isVisible?.()) return false;
            return await recursiveFilterForHiddenFieldConditional(closest);
        }

        let result = [];
        for (let a of all) {
            if (await recursiveFilterForHiddenFieldConditional(a)) {
                result.push(a);
            }
        }
        return result;
    }

    // CHECK FIELD PRESENCE

    checkFieldPresence(fields: CandidateField[]) {
        let elements = this.querySelectorAll<HTMLFormServiceElement>();
        let valid = true;
        let names = fields.map(f => f.name);
        let found = elements.map(el => el.name);
        for (let name of names) {
            if (!found.includes(name)) {
                console.warn('Name not found in <form>: ' + name);
                valid = false;
            }
        }
        return valid;
    }

    // LOAD

    loadObject(values: { [key: string]: AllValueTypes }) {
        this.loadEntries(Object.entries(values || {}), false);
    }

    loadObjectOnlyWhenEmpty(values: { [key: string]: AllValueTypes }) {
        this.loadEntries(Object.entries(values || {}), true);
    }

    private loadEntries(entries: [string, AllValueTypes][], onlyWhenEmpty: boolean) {
        let elements = this.querySelectorAll<HTMLFormServiceElement>();
        for (let el of elements) {
            this.loadSingle(el, this.getValueForName(el.name, entries), onlyWhenEmpty);
        }
    }

    private getValueForName(name: string, entries: [string, AllValueTypes][]): AllValueTypes {
        return entries.find(x => x?.[0] === name)?.[1];
    }

    private loadSingle(el: HTMLFormServiceElement, value: AllValueTypes, onlyWhenEmpty: boolean) {
        if (Array.isArray(value)) {
            if (value.length > 0 && isFileItem(value[0])) {
                if (!onlyWhenEmpty || !((el.files?.length || 0) > 0)) {
                    el.files = (value as CsbFileItem[]).filter(v => isFileItem(v));
                }
            } else {
                if (!onlyWhenEmpty || !((el.values?.length || 0) > 0)) {
                    el.updateValues(value.map(v => v?.toString()));
                }
            }
        } else if (value !== undefined) {
            if (value && typeof value !== 'boolean' && typeof value !== 'string') {
                value = '' + value;
            }
            if (!onlyWhenEmpty || el.value === undefined) {
                el.updateValue(value);
            }
        }
    }

    // VALIDATE

    async isInvalid({
        showValidationMsgs = true,
        scroll = true,
        scrollOffset = 200,
        scrollContainer = undefined
    }: {
        showValidationMsgs?: boolean,
        scroll?: boolean,
        scrollOffset?: number,
        scrollContainer?: HTMLElement
    }) {
        let elements = this.querySelectorAll<HTMLFormServiceElement>();
        elements = await this.filterConditionallyHiddenFields(elements);
        elements = await this.filterReadonlyFields(elements);
        let promises = [];

        for (let el of elements) {
            el.validationMsgEnabled = showValidationMsgs;
            promises.push(el.isInvalid());
        }
        let invalidResults = await Promise.all(promises);
        let invalidOnly = invalidResults.filter(v => v);

        if (scroll && invalidOnly.length > 0) {
            let invalidIndex = invalidResults.findIndex(v => v);
            let invalidFirst = elements[invalidIndex];
            FormService.scrollToInvalidElement(invalidFirst, scrollOffset, scrollContainer);
            FormService.focusInvalidElement(invalidFirst);
        }

        return invalidOnly.length ? invalidOnly : false; // All valid
    }

    // RETRIEVE

    async retrieveObject(names?: string[]) {
        let result: { [key: string]: AllValueTypes } = {};
        let elements = this.querySelectorAll<HTMLFormServiceElement>();
        elements = await this.filterConditionallyHiddenFields(elements);
        elements = await this.filterReadonlyFields(elements);
        for (let el of elements) {
            if (!names || names.includes(el.name)) {
                result[el.name] = this.retrieveSingle(el);
            }
        }
        return result;
    }

    private retrieveSingle(el: HTMLFormServiceElement): AllValueTypes {
        if (el.files !== undefined) return el.files;
        if (Array.isArray(el.values)) return el.values;
        return el.value;
    }

    // EVENTS

    private observerChanges: Observable<FormFieldChangedEvent> | undefined = undefined;
    observeChanges() {
        if (!this.observerChanges) {
            this.observerChanges = new Observable<FormFieldChangedEvent>(subscriber => {
                let host = this.getHostElement();
                if (host) {
                    host.addEventListener('changed', (ev: any) => {
                        subscriber.next(ev.detail);
                    });
                }
            });
        }
        return this.observerChanges;
    }

    // HELPER

    private static scrollToInvalidElement(invalidElement: HTMLElement, offset: number, container?: HTMLElement) {
        if (!invalidElement) return;

        let parent = document.body;
        let scroller: Window | HTMLElement = window;

        if (container) {
            parent = container;
            scroller = container;
        }

        const parentTop = parent.getBoundingClientRect().top;
        const elementTop = invalidElement.getBoundingClientRect().top;
        const elementPosition = elementTop - parentTop;
        scroller.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }

    private static focusInvalidElement(invalidElement: HTMLElement, preventScroll: boolean = true) {
        setTimeout(() => {
            invalidElement?.focus({ preventScroll });
        }, 200);
    }

    async resetFields() {
        let elements = this.querySelectorAll<HTMLFormServiceElement>();
        for (let el of elements) {
            if (el.reset) await el.reset();
        }
    }
}